/**
 * Controle de usuários (docs/spec/admin.md): a busca por nome ou e-mail, os filtros, a ordem e a
 * lista de contas. Clicar abre os detalhes; o "⋯", o botão direito ou segurar o dedo abrem as ações.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { formatBytes } from '../bytes.js';
import { PLAN_IDS, type AdminPlanFilter, type AdminUserFilter, type AdminUserRow, type AdminUserSort } from '../shared.js';
import { usePlanName } from '../plans/usePlanName.js';
import { Avatar } from '../social/Avatar.js';
import { useNow } from '../social/FriendChatWindow.js';
import { seenAgo } from '../social/friendsModel.js';
import { describeError, useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { Button, ComboBox, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { EmptyState } from '../ui/EmptyState.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import { availableActions } from './adminModel.js';
import { useAdminActions } from './useAdminActions.js';

const FILTERS: readonly AdminUserFilter[] = ['all', 'online', 'admins', 'suspended'];
const SORTS: readonly AdminUserSort[] = ['recent', 'name', 'seen'];
/** O filtro de plano (docs/spec/planos.md): todos, os pagos ou um plano. */
const PLAN_FILTERS: readonly AdminPlanFilter[] = ['all', 'paid', ...PLAN_IDS];

/** O plano na lista: o nome e, com prazo, "até 23/01/2027". */
export function usePlanText(): (plan: AdminUserRow['plan']) => string {
  const t = useT();
  const date = useDateText();
  const name = usePlanName();
  return (plan) => (plan.expiresAt === null ? name(plan.id) : `${name(plan.id)} · ${t('admin.plan.until', { date: date(plan.expiresAt) })}`);
}

/** Os selos ao lado do nome: administradora (da lista do servidor ou promovida) e suspensa. */
export function AdminBadges({ row }: { row: Pick<AdminUserRow, 'admin' | 'suspension'> }) {
  const t = useT();
  return (
    <>
      {row.admin ? (
        <span className="rd-adminBadge rd-adminBadge--admin" title={t(`admin.badge.admin.${row.admin}`)}>
          {t('admin.badge.admin')}
        </span>
      ) : null}
      {row.suspension ? <span className="rd-adminBadge rd-adminBadge--suspended">{t('admin.badge.suspended')}</span> : null}
    </>
  );
}

/** "Online agora", "visto há 3 h" ou "nunca entrou". */
export function useSeenText(): (row: Pick<AdminUserRow, 'online' | 'lastSeenAt'>) => string {
  const t = useT();
  const now = useNow(60_000);
  return (row) => {
    if (row.online) return t('admin.online');
    if (row.lastSeenAt === null) return t('admin.neverSeen');
    const seen = seenAgo(row.lastSeenAt, now);
    return t(seen.key, seen.params);
  };
}

export function useDateText(): (ms: number) => string {
  const language = useLanguage((s) => s.language);
  return (ms) => new Date(ms).toLocaleDateString(language, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function AdminUsers({
  selected,
  onSelect,
  version,
  onChanged,
  initialFilter = 'all',
  initialPlan = 'all',
}: {
  selected: string | null;
  onSelect: (id: string) => void;
  version: number;
  onChanged: () => void;
  /** o filtro com que a lista abre (a Visão geral abre "Online", "Suspensas" ou um plano) */
  initialFilter?: AdminUserFilter;
  initialPlan?: AdminPlanFilter;
}) {
  const t = useT();
  const token = useApp((s) => s.token);
  const meId = useApp((s) => s.user?.id ?? '');
  const run = useAdminActions();
  const press = useLongPress();
  const seen = useSeenText();
  const date = useDateText();
  const planText = usePlanText();
  const planLabel = usePlanName();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AdminUserFilter>(initialFilter);
  const [sort, setSort] = useState<AdminUserSort>('recent');
  const [plan, setPlan] = useState<AdminPlanFilter>(initialPlan);
  const [page, setPage] = useState<{ users: AdminUserRow[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; row: AdminUserRow } | null>(null);
  /** a lista da tela: muda a cada leitura do começo (outro filtro, outra busca, outra ordem) */
  const listRef = useRef(0);

  // a busca sai quando a pessoa para de digitar
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    listRef.current += 1;
    api.adminUsers(token, { q: search, filter, plan, sort, offset: 0 }).then(
      (result) => {
        if (!alive) return;
        setPage(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, search, filter, plan, sort, version]);

  const loadMore = async () => {
    if (!token || !page) return;
    const list = listRef.current;
    setLoadingMore(true);
    try {
      const next = await api.adminUsers(token, { q: search, filter, plan, sort, offset: page.users.length });
      // a lista mudou enquanto a página carregava: ela é de outro filtro
      if (list !== listRef.current) return;
      // uma conta nova no meio empurra a lista: a que já está na tela não se repete
      setPage((current) => (current ? { users: [...current.users, ...next.users.filter((row) => !current.users.some((have) => have.user.id === row.user.id))], total: next.total } : next));
    } catch (failure) {
      toast(describeError(failure), 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const entries = (row: AdminUserRow): MenuEntry[] => [
    { kind: 'title', label: row.user.displayName },
    { label: t('admin.details'), onSelect: () => onSelect(row.user.id) },
    { kind: 'separator' },
    ...availableActions(row, meId).map(({ action, refusal }): MenuEntry => ({
      label: t(`admin.action.${action}`),
      danger: action === 'suspend' || action === 'delete',
      disabled: refusal !== null,
      ...(refusal ? { hint: t(`admin.refusal.${refusal}`) } : {}),
      onSelect: () => void run(row, action).then((done) => done && onChanged()),
    })),
  ];

  return (
    <section className="rd-adminUsers" aria-label={t('admin.tab.users')}>
      <div className="rd-adminUsers__tools">
        <label className="rd-searchBox rd-adminUsers__search">
          <Icon name="search" size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('admin.users.search')} aria-label={t('admin.users.search')} />
        </label>
        <div className="rd-chips" role="tablist" aria-label={t('admin.users.filter')}>
          {FILTERS.map((f) => (
            <button key={f} type="button" role="tab" aria-selected={filter === f} className={`rd-chip${filter === f ? ' is-on' : ''}`} onClick={() => setFilter(f)}>
              {t(`admin.users.filter.${f}`)}
            </button>
          ))}
        </div>
        <ComboBox
          value={plan}
          onChange={setPlan}
          aria-label={t('admin.users.plan')}
          options={PLAN_FILTERS.map((p) => ({ value: p, label: p === 'all' || p === 'paid' ? t(`admin.users.plan.${p}`) : planLabel(p) }))}
        />
        <ComboBox value={sort} onChange={setSort} aria-label={t('admin.users.sort')} options={SORTS.map((s) => ({ value: s, label: t(`admin.users.sort.${s}`) }))} />
      </div>

      {error ? (
        <p className="rd-adminUsers__error" role="alert">
          {error}
        </p>
      ) : null}
      {!page && !error ? (
        <div className="rd-placeholder">
          <Spinner size={28} />
        </div>
      ) : null}
      {page ? (
        <div className="rd-adminUsers__body">
          <p className="rd-adminUsers__count">{t('admin.users.count', { n: page.total })}</p>
          {page.users.length ? (
            <div className="rd-adminTable" role="table" aria-label={t('admin.tab.users')}>
              <div className="rd-adminTable__row rd-adminTable__row--head" role="row">
                {(['account', 'email', 'created', 'seen', 'rooms', 'space', 'plan'] as const).map((col) => (
                  <span key={col} role="columnheader" className={`rd-adminTable__${col}`}>
                    {t(`admin.col.${col}`)}
                  </span>
                ))}
                <span role="columnheader" />
              </div>
              {page.users.map((row) => (
                <div
                  key={row.user.id}
                  className={`rd-adminTable__row${selected === row.user.id ? ' is-selected' : ''}`}
                  role="row"
                  tabIndex={0}
                  aria-label={row.user.displayName}
                  aria-selected={selected === row.user.id}
                  onClick={() => onSelect(row.user.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSelect(row.user.id);
                  }}
                  {...press.bind((anchor) => setMenu({ anchor, row }))}
                >
                  <span role="cell" className="rd-adminTable__account">
                    <Avatar name={row.user.displayName} color={row.user.color} size={28} userId={row.user.id} avatarVersion={row.user.avatarVersion} />
                    <span className="rd-adminTable__name">{row.user.displayName}</span>
                    <AdminBadges row={row} />
                  </span>
                  <span role="cell" className={`rd-adminTable__email${row.email ? '' : ' is-empty'}`}>
                    {row.email ?? '—'}
                  </span>
                  <span role="cell" className="rd-adminTable__created">
                    {date(row.user.createdAt)}
                  </span>
                  <span role="cell" className={`rd-adminTable__seen${row.online ? ' is-online' : ''}`}>
                    {seen(row)}
                  </span>
                  <span role="cell" className="rd-adminTable__rooms" title={t('admin.col.rooms')}>
                    {row.roomsOwned}
                  </span>
                  <span role="cell" className="rd-adminTable__space" title={t('admin.col.space')}>
                    {formatBytes(row.usedBytes)}
                  </span>
                  <span role="cell" className={`rd-adminTable__plan${row.plan.id === 'free' ? ' is-free' : ''}`} title={t('admin.col.plan')}>
                    {planText(row.plan)}
                  </span>
                  <span role="cell" className="rd-adminTable__actions">
                    <button
                      type="button"
                      className="rd-iconBtn rd-adminTable__more"
                      aria-label={t('admin.more', { name: row.user.displayName })}
                      onClick={(e) => {
                        e.stopPropagation();
                        const r = e.currentTarget.getBoundingClientRect();
                        setMenu({ anchor: { x: r.left, y: r.bottom }, row });
                      }}
                    >
                      ⋯
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon="user" title={t('admin.users.none')} />
          )}
          {page.users.length < page.total ? (
            <Button className="rd-adminUsers__more" busy={loadingMore} onClick={() => void loadMore()}>
              {t('admin.loadMore')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {menu ? <ContextMenu anchor={menu.anchor} label={menu.row.user.displayName} entries={entries(menu.row)} onClose={() => setMenu(null)} /> : null}
    </section>
  );
}
